import { type HTMLAttributes, forwardRef } from 'react';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md';
}

const variantStyles = {
  default: 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-200',
  success: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
  warning:
    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
  error: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'default', size = 'md', className = '', children, ...props },
  ref
) {
  return (
    <span
      ref={ref}
      className={`
        inline-flex items-center rounded-full font-medium
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {children}
    </span>
  );
});

// Specific badge for AI status
export interface AiStatusBadgeProps {
  status: 'none' | 'partial' | 'full';
  className?: string;
}

const aiStatusStyles = {
  none: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
  partial:
    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
  full: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
};

const aiStatusLabels = {
  none: { en: 'Human-Made', ja: '人間制作' },
  partial: { en: 'AI-Assisted', ja: 'AIアシスト' },
  full: { en: 'AI-Generated', ja: 'AI生成' },
};

export function AiStatusBadge({ status, className = '' }: AiStatusBadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center rounded-full px-3 py-1 text-sm font-medium
        ${aiStatusStyles[status]}
        ${className}
      `}
    >
      {aiStatusLabels[status].en}
    </span>
  );
}
